import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { Setting } from '../../types';
import { GlobalConfirmModal } from '../../components/modals/GlobalConfirmModal';
import { UnsavedChangesModal } from '../../components/modals/UnsavedChangesModal';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { dataCache } from '../../cache/DataCache';
import { notifyError, notifySuccess, errorMessage } from '../../utils/notify';
import {
  registerSettingsDraftGuard,
  type LeaveDecision,
  type SettingsLeaveReason,
} from './settingsDraft';

/**
 * 作品设定（「设定管理」面板）。
 *
 * 这一版按用户四轮要求重写，四条要求是同一件事的四个侧面：
 *
 * 1. **标题可改**：以前建好就再也改不了（PUT 带了 title，但界面上没有任何入口）。
 *    现在列表项右侧的「⋯」菜单里有「重命名」。
 * 2. **右侧不再是两个笔头/垃圾桶图标**（用户原话："太丑了"）—— 换成一个「⋯」菜单，
 *    点开才显示「重命名 / 删除」。菜单**portal 到 body**：列表滚动容器是
 *    `overflow: hidden/auto`，就地渲染的浮层会被裁掉（BookCard 的封面菜单踩过同一个坑）。
 * 3. **右侧就是编辑页**：不再有"正文只读 ↔ 点按钮/双击才变成输入框"两态。切到某条设定，
 *    右边**直接**是一个可以随便写的文本域（无边框、铺满面板，像一张纸）。
 * 4. **不自动保存**：改了什么只留在组件里，底部状态条如实显示「已保存 / 有未保存的改动」，
 *    用户按「保存」才落库（文本框里 Ctrl+S 同效）。切换设定、收起面板、离开创作页、
 *    关闭窗口之前都会问一句"保存 / 不保存 / 取消" —— 拦截逻辑见 `settingsDraft.ts`，
 *    三选一弹窗见 `components/modals/UnsavedChangesModal`。
 *
 * 双端：网页端与桌面端**共用这一份源码**（桌面端壳加载同一套前端产物，见
 * `apps/desktop/scripts/build-web.mjs`）。桌面端面板可能被放在**吸附窗口**里，
 * 那是另一个 SPA 实例 —— 跨窗口的"要不要先保存"由 `settingsDraft.ts` 的
 * BroadcastChannel 转达，见那个文件的说明。
 */

const OUTLINE_TITLE = '作品大纲';

/** 当前选中项：作品大纲 或 某条设定（用 id 而不是对象，列表刷新后标题自动跟着新数据走） */
type Selection = { kind: 'outline' } | { kind: 'setting'; id: string };

/** 弹窗文案：只说清"接下来会发生什么"，判断留给用户 */
function leaveMessage(reason: SettingsLeaveReason): string {
  switch (reason) {
    case 'switch':
      return '这条内容有未保存的改动。要先保存再切换吗？不保存的话，改动会丢掉。';
    case 'close-pane':
      return '这条内容有未保存的改动。要先保存再关闭设定管理吗？';
    case 'leave-page':
      return '设定管理里还有未保存的改动。要先保存再离开创作页吗？';
    case 'pane-move':
    default:
      return '这条内容有未保存的改动。要先保存吗？';
  }
}

/**
 * 列表项右侧的「⋯」菜单。
 *
 * 定位用**一次性快照 + fixed**（和 BookCard 的封面菜单同一套做法）：面板本身会被拖宽拖窄、
 * 列表还会滚动，跟着走的浮层没必要 —— 视口一变就关掉，比"停在旧坐标"更不容易出错。
 */
function RowMenu({ label, onRename, onDelete }: {
  label: string;
  onRename: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => { setOpen(false); setPos(null); }, []);

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // 菜单尺寸的估值只用于"往上还是往下弹"和防出屏，不需要精确
    const MENU_W = 132;
    const MENU_H = 82;
    const right = Math.min(
      Math.max(8, window.innerWidth - rect.right),
      Math.max(8, window.innerWidth - MENU_W - 8),
    );
    const below = rect.bottom + 4;
    const top = window.innerHeight - below >= MENU_H + 8
      ? below
      : Math.max(8, rect.top - MENU_H - 4);
    setPos({ top, right });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Esc 只关菜单，不再冒泡去关别的层（面板外可能还压着一个弹窗）
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
    };
    const onViewportChange = () => close();
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`ws-item-menu-btn${open ? ' is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`「${label}」更多操作`}
        title="更多操作"
        data-testid="ws-item-menu-btn"
        onClick={(e) => {
          e.stopPropagation();
          if (open) close(); else openMenu();
        }}
      >
        <MoreHorizontal size={14} aria-hidden />
      </button>
      {open && pos && ReactDOM.createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="ws-item-menu"
          data-testid="ws-item-menu"
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className="ws-item-menu-entry"
            data-testid="ws-item-rename"
            onClick={() => { close(); onRename(); }}
          >
            <Pencil size={13} aria-hidden />
            <span>重命名</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="ws-item-menu-entry is-danger"
            data-testid="ws-item-delete"
            onClick={() => { close(); onDelete(); }}
          >
            <Trash2 size={13} aria-hidden />
            <span>删除</span>
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * 设定标题弹窗：新增与**重命名**共用（两者只差标题、按钮文案和初值）。
 *
 * 校验三条：不能为空、不能叫「作品大纲」（那一行是固定入口）、不能与本作已有设定重名
 * （列表就按标题认人，重名两条肉眼分不出来）。
 */
function SettingTitleModal({ heading, confirmText, initialTitle, existingTitles, onClose, onSubmit }: {
  heading: string;
  confirmText: string;
  initialTitle: string;
  /** 已占用的标题（重命名时**不含自己**，由调用方过滤） */
  existingTitles: string[];
  onClose: () => void;
  onSubmit: (title: string) => void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const dismiss = useModalDismiss(onClose);

  const trimmed = title.trim();
  const reserved = trimmed === OUTLINE_TITLE;
  const duplicate = !reserved && existingTitles.includes(trimmed);
  const invalid = !trimmed || reserved || duplicate;
  const error = reserved
    ? `不得使用“${OUTLINE_TITLE}”作为设定名称`
    : duplicate
      ? '已存在同名设定'
      : '';

  const submit = () => {
    if (invalid) return;
    onSubmit(trimmed);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'var(--modal-mask)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} {...dismiss}>
      <div style={{
        backgroundColor: 'var(--card-bg)', padding: 20, borderRadius: 8, width: 400,
        // 弹窗是 position: fixed（相对视口，不随面板宽度缩放），窄窗口下仍要保证不撑破屏幕
        maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box',
        boxShadow: '0 4px 6px var(--shadow-color)', color: 'var(--text-color)',
      }}>
        <h2 style={{ marginBottom: 20, fontSize: 18, fontWeight: 600 }}>{heading}</h2>
        <input
          value={title}
          autoFocus
          onChange={e => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="设定名称（如：人物、地点、势力等）"
          data-testid="ws-title-input"
          style={{ width: '100%', padding: 10, marginBottom: 4, boxSizing: 'border-box', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', borderRadius: 4 }}
        />
        {error && (
          <div style={{ color: 'var(--danger-color)', fontSize: 11, marginBottom: 12 }}>{error}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: error ? 0 : 16 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', border: '1px solid var(--border-color)', backgroundColor: 'var(--card-bg)', color: 'var(--text-color)', borderRadius: 4, cursor: 'pointer' }}>取消</button>
          <button
            onClick={submit}
            disabled={invalid}
            data-testid="ws-title-confirm"
            style={{ padding: '8px 16px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: 4, cursor: invalid ? 'not-allowed' : 'pointer', opacity: invalid ? 0.5 : 1 }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export function WorldSettings() {
  const { id } = useParams();
  const bookId = id || '00000000-0000-0000-0000-000000000000';
  const { dataRefreshTrigger, triggerDataRefresh } = useWorkspace();
  const [settings, setSettings] = useState<Setting[]>(() => dataCache.getSettings(bookId) ?? []);
  const [outlineContent, setOutlineContent] = useState(() => dataCache.getOutline(bookId) ?? '');
  const [selection, setSelection] = useState<Selection | null>(null);
  /** 当前选中项的**已保存内容**（基线）；`draft` 与它不同 = 有未保存改动 */
  const [content, setContent] = useState('');
  /** 编辑区里的内容 —— 未保存的改动只活在这里，任何自动保存都没有 */
  const [draft, setDraft] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Setting | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(!dataCache.getSettings(bookId));
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; title: string; message: string; onConfirm: () => void } | null>(null);
  /** 正在等用户回答"保存 / 不保存 / 取消" */
  const [leavePrompt, setLeavePrompt] = useState<{ reason: SettingsLeaveReason; resolve: (decision: LeaveDecision) => void } | null>(null);

  const dirty = draft !== content;
  const selectedSetting = selection?.kind === 'setting'
    ? settings.find(s => s.id === selection.id) ?? null
    : null;
  const isOutlineSelected = selection?.kind === 'outline';

  /**
   * 给"离开守卫"和异步回调读的**最新值快照**。
   *
   * 守卫注册一次就常驻（不能每敲一个字就重注册），而它被触发时可能已经隔了好几轮渲染 ——
   * 闭包里的 `dirty` / `settings` 会是旧值。所以这些值都同步到 ref 里，守卫只读 ref。
   */
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const selectionRef = useRef<Selection | null>(selection);
  selectionRef.current = selection;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const outlineRef = useRef(outlineContent);
  outlineRef.current = outlineContent;
  /** 是否有一次保存正在进行（见 `handleSave` 的防重入） */
  const savingRef = useRef(false);

  const applySelection = useCallback((next: Selection) => {
    const base = next.kind === 'outline'
      ? outlineRef.current
      : (settingsRef.current.find(s => s.id === next.id)?.content ?? '');
    selectionRef.current = next;
    setSelection(next);
    setContent(base);
    setDraft(base);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [data, outline] = await Promise.all([
        window.api.getSettings(bookId),
        window.api.getOutline(bookId),
      ]);
      dataCache.setSettings(bookId, data);
      dataCache.setOutline(bookId, outline || '');
      setSettings(data);
      setOutlineContent(outline || '');
      // ref 也当场更新：紧接着可能要 `applySelection` 去取新列表里的内容
      // （state 要等下一次渲染才生效，这里不等 —— 否则会读到上一份列表）
      settingsRef.current = data;
      outlineRef.current = outline || '';

      /**
       * 选中项与"已保存内容"都以**服务端返回的数据**为准：
       * 选中的设定若已经不在列表里（别处删掉、或 AI 改过数据）→ 回到作品大纲，
       * 否则右侧会变成"请选择或创建一个设定"，看起来像坏了。首次加载也落在大纲上。
       */
      const previous = selectionRef.current;
      const previousId = previous?.kind === 'setting' ? previous.id : null;
      const current: Selection = previousId && data.some(s => s.id === previousId)
        ? { kind: 'setting', id: previousId }
        : { kind: 'outline' };
      setSelection(current);
      selectionRef.current = current;

      const selectedBase = current.kind === 'setting'
        ? (data.find(s => s.id === current.id)?.content ?? '')
        : (outline || '');
      setContent(selectedBase);
      // 有未保存改动时**不动**编辑区：刷新数据不该把用户写了一半的东西冲掉
      if (!dirtyRef.current) setDraft(selectedBase);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => {
    if (bookId) void loadData();
  }, [bookId, dataRefreshTrigger, loadData]);

  /**
   * 保存当前编辑区到服务端。成功返回 `true`（守卫用它决定"能不能离开"）。
   * 失败必须返回 false 并给出可见提示：否则用户会以为存上了。
   */
  const handleSave = async (): Promise<boolean> => {
    const target = selectionRef.current;
    if (!target) return false;
    // 防重入：守卫的"保存"与用户点「保存」可能撞在一起（连点、或确认框刚点完又点了按钮），
    // 两次 PUT 都带同一份内容，纯属多余（还可能让后一次盖掉前一次的响应顺序）
    if (savingRef.current) return false;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const text = draftRef.current;
      if (target.kind === 'outline') {
        await window.api.updateOutline({ bookId, content: text });
        setOutlineContent(text);
      } else {
        const setting = settingsRef.current.find(s => s.id === target.id);
        if (!setting) {
          notifyError('这条设定已经不在了，可能刚被删除');
          return false;
        }
        // 只写正文：标题由「重命名」那条路径负责（见 submitRename），这里带的是**已保存的**标题
        await window.api.updateSetting({ id: setting.id, title: setting.title, content: text });
      }
      setContent(text);
      await loadData();
      triggerDataRefresh();
      return true;
    } catch (err) {
      notifyError('保存失败：' + errorMessage(err));
      return false;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  /** 弹窗问用户：保存 / 不保存 / 取消（弹窗由本组件渲染，Promise 由用户点击解决） */
  const askUser = useCallback((reason: SettingsLeaveReason) => new Promise<LeaveDecision>((resolve) => {
    setLeavePrompt({ reason, resolve });
  }), []);

  const answerLeavePrompt = (decision: LeaveDecision) => {
    const prompt = leavePrompt;
    setLeavePrompt(null);
    prompt?.resolve(decision);
  };

  /** 先处理未保存改动，再执行 `proceed`（cancel = 什么都不做） */
  const withDraftSettled = async (proceed: () => void | Promise<void>) => {
    if (dirtyRef.current) {
      const decision = await askUser('switch');
      if (decision === 'cancel') return;
      if (decision === 'save' && !(await handleSaveRef.current())) return;
    }
    await proceed();
  };

  // 把"要不要先保存"的决定权交给面板之外的人（图标栏收起面板、返回书架、跨窗口…）
  useEffect(() => registerSettingsDraftGuard({
    isDirty: () => dirtyRef.current,
    ask: askUser,
    save: () => handleSaveRef.current(),
  }), [askUser]);

  /**
   * 刷新 / 关闭标签页 / 关闭窗口：浏览器原生确认框（文案由浏览器给，改不了）。
   * 桌面端主窗口的 × 走的是 Rust 的原生「收起/退出」对话框，不经过这里 —— 见 docs/DESKTOP.md。
   */
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const switchTo = (next: Selection) => {
    const same = next.kind === 'outline'
      ? selectionRef.current?.kind === 'outline'
      : selectionRef.current?.kind === 'setting' && selectionRef.current.id === next.id;
    if (same) return;
    void withDraftSettled(() => applySelection(next));
  };

  const handleCreateSetting = async (title: string) => {
    if (title === OUTLINE_TITLE) return;
    let createdId: string | undefined;
    try {
      const created = await window.api.createSetting({ bookId, title, content: '' });
      createdId = (created as { id?: string } | null)?.id;
    } catch (err) {
      notifyError('新增失败：' + errorMessage(err));
      return;
    }
    setShowCreateModal(false);
    await loadData();
    triggerDataRefresh();
    /**
     * 新建后**直接切到它**：否则右侧还留着上一条设定的内容，
     * 用户很可能以为自己正在写新建的那条（列表只多了一行，右侧毫无变化）。
     * 走 `withDraftSettled`：上一条若还有未保存的改动，会照常弹三选一，
     * 不会因为"新增"这个动作把草稿悄悄丢掉。
     */
    if (createdId) {
      const newId = createdId;
      await withDraftSettled(() => applySelection({ kind: 'setting', id: newId }));
    }
  };

  /** 重命名：只改标题，正文带的是**已保存**的那份 —— 编辑区里未保存的草稿不受影响 */
  const submitRename = async (title: string) => {
    const target = renameTarget;
    if (!target) return;
    const latest = settingsRef.current.find(s => s.id === target.id) ?? target;
    try {
      await window.api.updateSetting({ id: latest.id, title, content: latest.content ?? '' });
    } catch (err) {
      notifyError('重命名失败：' + errorMessage(err));
      return;
    }
    setRenameTarget(null);
    await loadData();
    triggerDataRefresh();
    notifySuccess(`已重命名为「${title}」`);
  };

  const requestDelete = (setting: Setting) => {
    const isCurrent = selectionRef.current?.kind === 'setting' && selectionRef.current.id === setting.id;
    const dropsDraft = isCurrent && dirtyRef.current;
    setConfirmModal({
      show: true,
      title: '删除设定',
      message: dropsDraft
        ? `「${setting.title}」还有未保存的改动，删除后不会保留。确定删除吗？此操作不可撤销。`
        : `确定删除「${setting.title}」吗？此操作不可撤销。`,
      onConfirm: async () => {
        try {
          await window.api.deleteSetting(setting.id);
        } catch (err) {
          notifyError('删除失败：' + errorMessage(err));
          setConfirmModal(null);
          return;
        }
        setConfirmModal(null);
        // 删掉的正是当前编辑的那条 → 回到作品大纲（草稿随之作废，上面已经提示过）
        if (isCurrent) applySelection({ kind: 'outline' });
        await loadData();
        triggerDataRefresh();
        notifySuccess('已删除设定');
      },
    });
  };

  /** 作品大纲那一行：固定入口，没有重命名/删除，也没有「⋯」菜单 */
  const renderOutlineItem = () => (
    <div
      key="__outline__"
      // 内边距与选中态在 settingManager.css 的 .ws-item（窄栏要收窄，内联样式盖不住）
      className={`ws-item${isOutlineSelected ? ' is-selected' : ''}`}
      data-testid="ws-item-outline"
      onClick={() => switchTo({ kind: 'outline' })}
      title={OUTLINE_TITLE}
    >
      <div className="ws-item-title">{OUTLINE_TITLE}</div>
    </div>
  );

  /** 一条设定：标题 + 「⋯」（重命名 / 删除） */
  const renderSettingItem = (setting: Setting) => (
    <div
      key={setting.id}
      className={`ws-item${selectedSetting?.id === setting.id ? ' is-selected' : ''}`}
      data-testid="ws-item-setting"
      onClick={() => switchTo({ kind: 'setting', id: setting.id })}
      title={setting.title}
    >
      <div className="ws-item-title">{setting.title}</div>
      <RowMenu
        label={setting.title}
        onRename={() => setRenameTarget(setting)}
        onDelete={() => requestDelete(setting)}
      />
    </div>
  );

  return (
    <div className="ws-root">
      {/*
        窄栏适配的宿主：`.ws-frame` 是具名容器（container-type: inline-size）。
        head / body 都是它的后代，所以 @container 规则按**面板实际宽度**生效，
        与视口宽度、窗口缩放都无关（面板宽度由用户拖拽决定，见 settingManager.css 末尾）。

        四个弹窗（新增 / 重命名 / 未保存 / 删除确认）刻意留在容器**外面**：
        container-type 会带来 layout containment，fixed 定位的后代会以该容器为包含块 ——
        弹窗若落在容器内部，就会被限制在面板范围里（240px 面板装 400px 弹窗）。
        「⋯」菜单更是直接 portal 到 body（见 RowMenu），列表滚动容器裁不到它。
      */}
      <div className="ws-frame">
        {/* 标题行 */}
        <div className="ws-head">
          作品设定
        </div>

        <div className="ws-body">
          {/* 左侧设定列表 */}
          <div className="ws-list">
            <div className="ws-list-scroll">
              {/* 作品大纲——固定在列表顶部，不可重命名/删除 */}
              {renderOutlineItem()}
              {/* 分隔线 */}
              <div className="ws-sep" />
              {settings.map(setting => renderSettingItem(setting))}
            </div>

            {/* 新增按钮 */}
            <div className="ws-list-foot">
              <button
                className="ws-add-btn"
                onClick={() => setShowCreateModal(true)}
              >
                + 新增
              </button>
            </div>
          </div>

          {/* 右侧内容：**就是编辑页**（不是"只读正文 + 进入编辑"两态） */}
          <div className="ws-detail">
            {loading ? (
              <div className="ws-placeholder">加载中…</div>
            ) : (isOutlineSelected || selectedSetting) ? (
              <div className="ws-detail-edit">
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    // Ctrl/Cmd+S 手动保存（只有焦点在编辑区里才接管，不抢章节编辑器的同名快捷键）
                    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.code === 'KeyS')) {
                      e.preventDefault();
                      void handleSaveRef.current();
                    }
                  }}
                  placeholder={isOutlineSelected ? '在这里写整部作品的大纲…' : '在这里写这条设定的内容…'}
                  className="ws-editor"
                  data-testid="ws-editor"
                  spellCheck={false}
                />
                <div className="ws-edit-actions">
                  <span
                    className={`ws-save-status${dirty ? ' is-dirty' : ''}`}
                    data-testid="ws-save-status"
                  >
                    {isSaving ? '保存中…' : dirty ? '有未保存的改动' : '已保存'}
                  </span>
                  <button
                    className="ws-save-btn"
                    onClick={() => { void handleSaveRef.current(); }}
                    disabled={isSaving || !dirty}
                    data-testid="ws-save"
                  >
                    {isSaving ? '保存中…' : '保存'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="ws-placeholder">
                请选择或创建一个设定以开始编辑
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <SettingTitleModal
          heading="新增设定"
          confirmText="确定"
          initialTitle=""
          existingTitles={settings.map(s => s.title)}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(title) => { void handleCreateSetting(title); }}
        />
      )}

      {renameTarget && (
        <SettingTitleModal
          heading="重命名设定"
          confirmText="保存"
          initialTitle={renameTarget.title}
          existingTitles={settings.filter(s => s.id !== renameTarget.id).map(s => s.title)}
          onClose={() => setRenameTarget(null)}
          onSubmit={(title) => { void submitRename(title); }}
        />
      )}

      {leavePrompt && (
        <UnsavedChangesModal
          show
          message={leaveMessage(leavePrompt.reason)}
          saving={isSaving}
          onSave={() => answerLeavePrompt('save')}
          onDiscard={() => answerLeavePrompt('discard')}
          onCancel={() => answerLeavePrompt('cancel')}
        />
      )}

      {confirmModal && (
        <GlobalConfirmModal
          show={confirmModal.show}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}
